import {
	CheckCircleOutlined,
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	MoreOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Dropdown, Popconfirm, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type { ComponentProps } from "react";
import { useBrigadeList } from "@/modules/brigades/hooks/useBrigade";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import {
	TASK_PRIORITY_LABELS,
	TASK_PRIORITY_OPTIONS,
	TASK_STATUS_COLORS,
	TASK_STATUS_OPTIONS,
} from "../constants";
import { useDeleteTask, useTasks } from "../hooks/useTasks";
import { useTasksStore } from "../store/tasks.store";
import type { Task, TaskStatus, TaskTableProps } from "../types";

type RangePickerOnChange = NonNullable<ComponentProps<typeof DatePicker.RangePicker>["onChange"]>;

export function TaskTable({
	page,
	limit,
	onPageChange,
	organizationId,
	scopeOrganizationId,
	filters,
	onFiltersChange,
	priorityLocked,
	onRowClick,
}: TaskTableProps) {
	const { data, isLoading } = useTasks({
		page,
		limit,
		organizationId,
		...filters,
	});
	const { openStatusModal, openEditModal } = useTasksStore();
	const { mutate: removeTask } = useDeleteTask();

	const { data: brigadesData } = useBrigadeList({
		organizationId: scopeOrganizationId ?? "",
		page: 1,
		limit: 100,
	});
	const brigades = brigadesData?.data?.items ?? [];

	const setFilter = (patch: Partial<NonNullable<typeof filters>>) => {
		onFiltersChange?.({ ...filters, ...patch });
	};

	const dueDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
		filters?.dueDateFrom && filters?.dueDateTo
			? [dayjs(filters.dueDateFrom), dayjs(filters.dueDateTo)]
			: null;

	const handleDueDateRangeChange: RangePickerOnChange = (dates) => {
		if (dates?.[0] && dates?.[1]) {
			setFilter({
				dueDateFrom: dates[0].startOf("day").toISOString(),
				dueDateTo: dates[1].endOf("day").toISOString(),
			});
		} else {
			setFilter({ dueDateFrom: undefined, dueDateTo: undefined });
		}
	};

	const columns: ColumnsType<Task> = [
		{
			title: "№",
			key: "index",
			width: 60,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Sarlavha",
			dataIndex: "title",
			key: "title",
			width: 260,
			ellipsis: true,
		},
		{
			title: "Turi",
			key: "type",
			width: 120,
			render: (_: unknown, record: Task) => record.type?.name ?? record.typeId,
		},
		{
			title: "Ustuvorlik",
			dataIndex: "priority",
			key: "priority",
			width: 100,
			render: (p: string) => TASK_PRIORITY_LABELS[p as keyof typeof TASK_PRIORITY_LABELS] ?? p,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 180,
			render: (status: TaskStatus, record: Task) => (
				<Space size={4}>
					<StatusTag tone={TASK_STATUS_COLORS[status]}>
						{TASK_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status}
					</StatusTag>
					{record.isOverdue && <Tag color="red">Muddati o'tgan</Tag>}
				</Space>
			),
		},
		{
			title: "Brigada",
			key: "brigade",
			width: 160,
			ellipsis: true,
			render: (_: unknown, record: Task) => record.brigade?.name ?? "—",
		},
		{
			title: "Tashkilot",
			dataIndex: "organizationName",
			key: "organizationName",
			width: 150,
			ellipsis: true,
		},
		{
			title: "Tugash sanasi",
			dataIndex: "dueDate",
			key: "dueDate",
			width: 120,
			render: (date: string | null) =>
				date
					? new Date(date).toLocaleDateString("uz-UZ", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
						})
					: "-",
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: Task) => (
				// biome-ignore lint/a11y/useKeyWithClickEvents: stop row navigation
				// biome-ignore lint/a11y/noStaticElementInteractions: stop row navigation
				<div onClick={(e) => e.stopPropagation()}>
					<Dropdown
						menu={{
							items: [
								{
									key: "view",
									icon: <EyeOutlined />,
									label: "Batafsil",
									onClick: () => onRowClick?.(record),
								},
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
								{
									key: "status",
									icon: <CheckCircleOutlined />,
									label: "Statusni yangilash",
									onClick: () => openStatusModal(record),
								},
								{
									key: "delete",
									danger: true,
									label: (
										// biome-ignore lint/a11y/useKeyWithClickEvents: dropdown menu item
										// biome-ignore lint/a11y/noStaticElementInteractions: dropdown menu item
										<div onClick={(e) => e.stopPropagation()}>
											<Popconfirm
												title="O'chirish"
												description="Rostdan ham o'chirmoqchimisiz?"
												onConfirm={() => removeTask(record.id)}
												okText="Ha"
												cancelText="Yo'q"
												okButtonProps={{ danger: true }}
											>
												<div
													style={{
														width: "100%",
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}
												>
													<DeleteOutlined />
													<span>O&apos;chirish</span>
												</div>
											</Popconfirm>
										</div>
									),
								},
							],
						}}
						trigger={["click"]}
						placement="bottomRight"
					>
						<Button type="text" icon={<MoreOutlined />} size="small" />
					</Dropdown>
				</div>
			),
		},
	];

	return (
		<div>
			{onFiltersChange && (
				<TableToolbar
					filters={
						<>
							<span className="text-ink-soft">Status:</span>
							<Select
								placeholder="Barchasi"
								allowClear
								value={filters?.status}
								onChange={(status) => setFilter({ status })}
								options={TASK_STATUS_OPTIONS}
								style={{ width: 150 }}
							/>
							{!priorityLocked && (
								<>
									<span className="text-ink-soft">Ustuvorlik:</span>
									<Select
										placeholder="Barchasi"
										allowClear
										value={filters?.priority}
										onChange={(priority) => setFilter({ priority })}
										options={TASK_PRIORITY_OPTIONS}
										style={{ width: 140 }}
									/>
								</>
							)}
							<span className="text-ink-soft">Brigada:</span>
							<Select
								placeholder="Barchasi"
								allowClear
								showSearch
								optionFilterProp="label"
								value={filters?.brigadeId}
								onChange={(brigadeId) => setFilter({ brigadeId })}
								options={brigades.map((b) => ({ value: b.id, label: b.name }))}
								style={{ width: 170 }}
							/>
							<span className="text-ink-soft">Muddat:</span>
							<DatePicker.RangePicker
								allowClear
								value={dueDateRange}
								onChange={handleDueDateRangeChange}
								format="DD.MM.YYYY"
								placeholder={["Boshlanish", "Tugash"]}
								style={{ width: 240 }}
							/>
						</>
					}
				/>
			)}
			<Table
				columns={columns}
				dataSource={data?.data?.items}
				loading={isLoading}
				rowKey="id"
				locale={{
					emptyText: <EmptyState description="Topshiriqlar topilmadi" />,
				}}
				onRow={(record) => ({
					onClick: () => onRowClick?.(record),
					style: onRowClick ? { cursor: "pointer" } : undefined,
				})}
				pagination={tablePagination({
					page,
					limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					sizeChanger: false,
				})}
				scroll={{ x: "max-content" }}
				size="middle"
			/>
		</div>
	);
}
